import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';
import { Paper, Typography, Box, CircularProgress, Alert, FormControl, InputLabel, Select, MenuItem, useTheme, useMediaQuery } from '@mui/material';
import { analyticsApi } from '../../services/api';

interface MonthlyData {
  month: string;
  amount: number;
  year: number;
}

interface MonthOverMonthData {
  current_year: MonthlyData[];
  previous_year: MonthlyData[];
}

const MonthOverMonthChart: React.FC = () => {
  const [data, setData] = useState<MonthOverMonthData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const availableYears = [2023, 2024, 2025];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await analyticsApi.getMonthOverMonth(selectedYear);
        setData(response);
        setError(null);
      } catch (err) {
        setError('Failed to load month-over-month data');
        console.error('Error fetching month-over-month data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [selectedYear]);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-CH', {
      style: 'currency',
      currency: 'CHF',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  // Combine current and previous year data for chart
  const chartData = data ? data.current_year.map((current, index) => ({
    month: current.month.substring(0, 3), // Abbreviate month names
    currentYear: current.amount,
    previousYear: data.previous_year[index]?.amount || 0,
    fullMonth: current.month
  })) : [];

  if (loading) {
    return (
      <Paper sx={{ p: 3, height: 450, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  if (error) {
    return (
      <Paper sx={{ p: 3, height: 450 }}>
        <Alert severity="error">{error}</Alert>
      </Paper>
    );
  }

  return (
    <Box sx={{ width: '100%', overflow: 'hidden' }}>
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: isMobile ? 'flex-start' : 'center', 
        mb: 2,
        flexDirection: isMobile ? 'column' : 'row',
        gap: isMobile ? 1 : 0
      }}>
        <Typography variant="h6" sx={{ 
          color: 'primary.main', 
          fontWeight: 'bold',
          fontSize: { xs: '1rem', md: '1.25rem' }
        }}>
          Month-over-Month Expenses
        </Typography>
        <FormControl size="small" sx={{ minWidth: isMobile ? '100%' : 120 }}>
          <InputLabel>Year</InputLabel>
          <Select
            value={selectedYear}
            label="Year"
            onChange={(e) => setSelectedYear(Number(e.target.value))}
          >
            {availableYears.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>
      <Box sx={{ 
        height: { xs: 280, sm: 320, md: 350 }, 
        width: '100%',
        mt: 2,
        overflow: 'hidden'
      }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart 
            data={chartData} 
            margin={{ 
              top: 10, 
              right: isMobile ? 5 : 15, 
              left: isMobile ? 5 : 10, 
              bottom: isMobile ? 60 : 40 
            }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="month"
              stroke="#666"
              fontSize={isMobile ? 10 : 12}
              interval={isMobile ? 1 : 0}
              angle={isMobile ? -45 : 0}
              textAnchor={isMobile ? "end" : "middle"}
              height={isMobile ? 80 : 60}
            />
            <YAxis 
              tickFormatter={(value) => isMobile ? `${Math.round(value/1000)}k` : formatCurrency(value)}
              stroke="#666"
              fontSize={isMobile ? 10 : 12}
              width={isMobile ? 40 : 80}
            />
            <Tooltip
              formatter={(value: number, name: string) => [
                formatCurrency(value), 
                name === 'currentYear' ? `${selectedYear}` : `${selectedYear - 1}`
              ]}
              labelFormatter={(month: string) => `Month: ${chartData.find(d => d.month === month)?.fullMonth || month}`}
              contentStyle={{
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                border: '1px solid #e0e0e0',
                borderRadius: '8px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                padding: isMobile ? '8px' : '12px',
                fontSize: isMobile ? '0.75rem' : '0.875rem'
              }}
            />
            {!isMobile && <Legend />}
            <Line 
              type="monotone" 
              dataKey="currentYear" 
              stroke="#3f51b5" 
              strokeWidth={isMobile ? 2 : 3}
              name={selectedYear.toString()}
              dot={{ fill: '#3f51b5', strokeWidth: 1, r: isMobile ? 3 : 4 }}
              activeDot={{ r: isMobile ? 4 : 6, stroke: '#3f51b5', strokeWidth: 2 }}
            />
            <Line 
              type="monotone" 
              dataKey="previousYear" 
              stroke="#9c27b0" 
              strokeWidth={isMobile ? 2 : 3}
              strokeDasharray="5 5"
              name={(selectedYear - 1).toString()}
              dot={{ fill: '#9c27b0', strokeWidth: 1, r: isMobile ? 3 : 4 }}
              activeDot={{ r: isMobile ? 4 : 6, stroke: '#9c27b0', strokeWidth: 2 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </Box>
      {isMobile && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 2, 
          mt: 1,
          flexWrap: 'wrap'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 2, bgcolor: '#3f51b5', borderRadius: 1 }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{selectedYear}</Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 2, bgcolor: '#9c27b0', borderRadius: 1, 
              backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 2px, white 2px, white 4px)' 
            }} />
            <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>{selectedYear - 1}</Typography>
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default MonthOverMonthChart;